import { jsPDF as JsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { InvoiceInterface } from '../../../interface/invoice-interface';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import FormatDuration from '../../../shared-functions/FormatDuration';

/**
 * Helper to convert duration string (e.g. "HH:MM:SS" or seconds) to total seconds.
 * @param {string | number} [duration] - The duration value.
 * @returns {number} The total seconds.
 */
const parseDurationToSeconds = (duration?: string | number): number => {
  if (!duration) return 0;
  if (typeof duration === 'number') return duration;
  if (typeof duration === 'string' && duration.includes(':')) {
    const parts = duration.split(':').map((p) => Number(p) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }
  const numericValue = Number(duration);
  return !Number.isNaN(numericValue) ? numericValue : 0;
};

/**
 * Generate and download a PDF document for a single invoice.
 * @param {InvoiceInterface} invoice - The invoice data object.
 */
const downloadSingleInvoicePdf = (invoice: InvoiceInterface) => {
  const document = new JsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const currency = invoice.currency || 'INR';
  const details = invoice.chargingDetails;
  const breakdown = details?.costBreakdown;

  // Header Title
  document.setFontSize(20);
  document.setTextColor(40, 40, 40);
  document.text('TAX INVOICE', 14, 20);

  // Invoice Meta
  document.setFontSize(10);
  document.setTextColor(100, 100, 100);
  document.text(`Invoice No: ${invoice.invoiceNumber || '-'}`, 14, 28);
  document.text(`Invoice Date: ${ConvertUTCtoDateTime(invoice.invoiceDate)}`, 14, 34);
  document.text(`Financial Year: ${invoice.financialYear || '-'}`, 14, 40);

  // Divider
  document.setDrawColor(220, 220, 220);
  document.line(14, 45, 196, 45);

  // Invoice From & Invoice To Box
  document.setFontSize(11);
  document.setTextColor(40, 40, 40);
  document.setFont('helvetica', 'bold');
  document.text('Invoice From (Provider)', 14, 52);
  document.text('Invoice To (Customer)', 110, 52);

  document.setFont('helvetica', 'normal');
  document.setFontSize(9.5);
  document.setTextColor(80, 80, 80);

  // From
  document.text(`Name: ${invoice.invoiceFrom?.name || 'Energic'}`, 14, 58);
  document.text(`Email: ${invoice.invoiceFrom?.email || '-'}`, 14, 63);
  document.text(`GSTIN: ${invoice.invoiceFrom?.gstin || '-'}`, 14, 68);
  document.text(`Address: ${invoice.invoiceFrom?.billingAddress || '-'}`, 14, 73, { maxWidth: 85 });

  // To
  document.text(`Name: ${invoice.invoiceTo?.name || '-'}`, 110, 58);
  document.text(`Email: ${invoice.invoiceTo?.email || '-'}`, 110, 63);
  document.text(`Mobile: ${invoice.invoiceTo?.mobile || '-'}`, 110, 68);
  document.text(`Location: ${invoice.invoiceTo?.chargingLocation || '-'}`, 110, 73, { maxWidth: 85 });

  // Charger Info Section
  document.line(14, 85, 196, 85);
  document.setFont('helvetica', 'bold');
  document.setFontSize(11);
  document.setTextColor(40, 40, 40);
  document.text('Charging & Session Details', 14, 92);

  autoTable(document, {
    startY: 96,
    theme: 'grid',
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    head: [['Description', 'Details']],
    body: [
      ['Session ID', invoice.sessionId || '-'],
      ['Transaction ID', invoice.transactionId || '-'],
      ['User Custom ID', invoice.userCustomId || '-'],
      ['Charger ID / Station ID', `${invoice.chargerInfo?.chargerId || '-'} (${invoice.chargerInfo?.stationId || '-'})`],
      ['Connector Type & Rating', `${invoice.chargerInfo?.connectorType || '-'} / ${invoice.chargerInfo?.connectorRating || '-'}`],
      ['Start Time', invoice.chargingDetails?.startTime ? ConvertUTCtoDateTime(invoice.chargingDetails.startTime) : '-'],
      ['End Time', invoice.chargingDetails?.endTime ? ConvertUTCtoDateTime(invoice.chargingDetails.endTime) : '-'],
      ['Charging Duration', FormatDuration(parseDurationToSeconds(invoice.chargingDetails?.chargingDuration), '-')],
      ['Energy Consumed', `${invoice.chargingDetails?.energyUsed || 0} kWh`],
    ],
  });

  const finalY = (document as any).lastAutoTable.finalY + 8;

  // Cost Breakdown Table
  document.setFont('helvetica', 'bold');
  document.setFontSize(11);
  document.setTextColor(40, 40, 40);
  document.text('Cost Breakdown', 14, finalY);

  const consumptionCost = breakdown?.consumptionCost ?? (details?.invoiceAmountPaid || 0);
  const platformFee = breakdown?.platformFee ?? 0;
  const sessionFee = breakdown?.sessionFee ?? 0;
  const totalAmount = details?.invoiceAmountPaid || 0;

  autoTable(document, {
    startY: finalY + 4,
    theme: 'striped',
    headStyles: { fillColor: [46, 204, 113], textColor: 255 },
    head: [['Item', 'Amount']],
    body: [
      ['Consumption Cost', `${Number(consumptionCost || 0).toFixed(2)} ${currency}`],
      ['Platform Fee', `${Number(platformFee || 0).toFixed(2)} ${currency}`],
      ['Session Fee', `${Number(sessionFee || 0).toFixed(2)} ${currency}`],
      ['Total Amount Paid', `${Number(totalAmount || 0).toFixed(2)} ${currency}`],
    ],
  });

  const fileName = `Invoice_${invoice.invoiceNumber?.replace(/\//g, '_') || invoice._id}.pdf`;
  document.save(fileName);
};

export default downloadSingleInvoicePdf;
